import React, { useState } from 'react';
import {
  useFinancialModel,
  QUARTERS,
  StaffPosition,
  RevenueSettings,
  CostSettings,
} from './FinancialModelContext';

type TabType = 'summary' | 'staff' | 'revenue' | 'costs' | 'settings';

const GTMv2Slide05FinancialCalculator: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('summary');

  // Use shared financial model context
  const {
    seedInvestment,
    setSeedInvestment,
    staffPositions,
    setStaffPositions,
    revenueSettings,
    setRevenueSettings,
    costSettings,
    setCostSettings,
    calculations,
  } = useFinancialModel();

  const formatCurrency = (value: number, short = false) => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (short) {
      if (absValue >= 1000000) return `${sign}$${(absValue / 1000000).toFixed(1)}M`;
      if (absValue >= 1000) return `${sign}$${(absValue / 1000).toFixed(0)}K`;
    }
    return `${sign}$${absValue.toLocaleString()}`;
  };

  const updateStaffFTE = (positionId: string, quarterIndex: number, delta: number, singleCell: boolean = false) => {
    setStaffPositions(prev => prev.map(pos =>
      pos.id === positionId
        ? {
            ...pos,
            fteByQuarter: pos.fteByQuarter.map((v, i) => {
              if (singleCell) {
                return i === quarterIndex ? Math.max(0, Math.min(10, v + delta)) : v;
              }
              return i >= quarterIndex ? Math.max(0, Math.min(10, v + delta)) : v;
            })
          }
        : pos
    ));
  };

  const updateStaffSalary = (positionId: string, salary: number) => {
    setStaffPositions(prev => prev.map(pos =>
      pos.id === positionId ? { ...pos, baseSalary: salary } : pos
    ));
  };

  const updateStaffYearlyIncrease = (positionId: string, percent: number) => {
    setStaffPositions(prev => prev.map(pos =>
      pos.id === positionId ? { ...pos, yearlyIncreasePercent: percent } : pos
    ));
  };

  const updateStaffName = (positionId: string, name: string) => {
    setStaffPositions(prev => prev.map(pos =>
      pos.id === positionId ? { ...pos, name } : pos
    ));
  };

  const addStaffPosition = () => {
    const newId = `position-${Date.now()}`;
    setStaffPositions(prev => [...prev, {
      id: newId,
      name: 'New Position',
      baseSalary: 100000,
      yearlyIncreasePercent: 3.5,
      fteByQuarter: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    }]);
  };

  const removeStaffPosition = (positionId: string) => {
    setStaffPositions(prev => prev.filter(pos => pos.id !== positionId));
  };

  const tabs: { id: TabType; label: string }[] = [
    { id: 'summary', label: 'Summary' },
    { id: 'staff', label: 'Staff' },
    { id: 'revenue', label: 'Revenue' },
    { id: 'costs', label: 'Costs' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="flex flex-col h-full relative overflow-hidden">
      {/* Atmospheric background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(139, 92, 246, 0.15) 0%, transparent 50%), radial-gradient(ellipse 100% 50% at 50% 100%, rgba(14, 165, 233, 0.1) 0%, transparent 50%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full px-6 py-4">
        {/* Header with tabs */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-3xl font-bold text-white tracking-tight">
              Financial Model
            </h2>
            <p className="text-sm text-[#94a3b8]">
              3-year projections with full variability control
            </p>
          </div>

          {/* Seed Investment Control */}
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs text-[#a78bfa] uppercase tracking-wider">Seed Investment</div>
              <div className="text-2xl font-bold text-white">{formatCurrency(seedInvestment, true)}</div>
            </div>
            <input
              type="range"
              min={500000}
              max={3000000}
              step={100000}
              value={seedInvestment}
              onChange={(e) => setSeedInvestment(parseInt(e.target.value))}
              className="w-32 h-2 rounded appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #8b5cf6 0%, #8b5cf6 ${((seedInvestment - 500000) / 2500000) * 100}%, #334155 ${((seedInvestment - 500000) / 2500000) * 100}%, #334155 100%)`,
              }}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-3 border-b border-white/10 pb-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-all ${
                activeTab === tab.id
                  ? 'bg-white/10 text-white border-b-2 border-[#8b5cf6]'
                  : 'text-[#94a3b8] hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'summary' && (
            <SummaryTab calculations={calculations} formatCurrency={formatCurrency} seedInvestment={seedInvestment} />
          )}
          {activeTab === 'staff' && (
            <StaffTab
              positions={staffPositions}
              onUpdateFTE={updateStaffFTE}
              onUpdateSalary={updateStaffSalary}
              onUpdateYearlyIncrease={updateStaffYearlyIncrease}
              onUpdateName={updateStaffName}
              onAdd={addStaffPosition}
              onRemove={removeStaffPosition}
              formatCurrency={formatCurrency}
            />
          )}
          {activeTab === 'revenue' && (
            <RevenueTab
              settings={revenueSettings}
              onUpdate={setRevenueSettings}
              formatCurrency={formatCurrency}
            />
          )}
          {activeTab === 'costs' && (
            <CostsTab
              settings={costSettings}
              onUpdate={setCostSettings}
              formatCurrency={formatCurrency}
              quarterlyData={calculations.quarterData}
            />
          )}
          {activeTab === 'settings' && (
            <SettingsTab
              costSettings={costSettings}
              onUpdateCosts={setCostSettings}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Summary Tab Component
const SummaryTab: React.FC<{
  calculations: any;
  formatCurrency: (v: number, short?: boolean) => string;
  seedInvestment: number;
}> = ({ calculations, formatCurrency, seedInvestment }) => {
  // Cash position chart range
  const maxCash = Math.max(seedInvestment, ...calculations.quarterData.map((q: any) => q.cashPosition));
  const minCash = Math.min(0, ...calculations.quarterData.map((q: any) => q.cashPosition));
  const chartMax = maxCash;
  const chartMin = minCash;
  const chartRange = chartMax - chartMin || 1;

  // Revenue chart range (for stacked area)
  const maxQuarterlyRevenue = Math.max(...calculations.quarterData.map((q: any) => q.b2cRevenue + q.b2bRevenue));
  const revenueChartMax = maxQuarterlyRevenue * 1.1; // 10% padding

  return (
    <div className="h-full flex gap-6">
      {/* Left side - Key metrics */}
      <div className="w-64 flex flex-col gap-4">
        {/* Primary metric - Bridge or Final Cash */}
        <div
          className="p-5 rounded-lg"
          style={{
            background: calculations.needsBridgeRound
              ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.05) 100%)'
              : 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.05) 100%)',
            border: `1px solid ${calculations.needsBridgeRound ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`,
          }}
        >
          <div className={`text-sm uppercase tracking-wider mb-1 ${calculations.needsBridgeRound ? 'text-[#ef4444]' : 'text-[#10b981]'}`}>
            {calculations.needsBridgeRound ? 'Bridge Round Required' : 'Runway Secured'}
          </div>
          <div className={`text-4xl font-bold ${calculations.needsBridgeRound ? 'text-[#ef4444]' : 'text-[#10b981]'}`}>
            {calculations.needsBridgeRound
              ? formatCurrency(calculations.bridgeAmount, true)
              : formatCurrency(calculations.finalCash, true)}
          </div>
          {calculations.needsBridgeRound && calculations.cashOutQuarter && (
            <div className="text-sm text-[#94a3b8] mt-2">Cash out: {calculations.cashOutQuarter}</div>
          )}
        </div>

        {/* 3-Year Totals */}
        <div
          className="p-4 rounded-lg"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="text-xs text-[#64748b] uppercase tracking-wider mb-3">3-Year Totals</div>
          <div className="space-y-3">
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-[#94a3b8]">Revenue</span>
              <span className="text-xl font-bold text-white">{formatCurrency(calculations.totalRevenue, true)}</span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-[#94a3b8]">Net P&L</span>
              <span className={`text-xl font-bold ${calculations.totalNet >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                {formatCurrency(calculations.totalNet, true)}
              </span>
            </div>
          </div>
        </div>

        {/* Yearly breakdown */}
        <div
          className="p-4 rounded-lg flex-1"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="text-xs text-[#64748b] uppercase tracking-wider mb-3">Net by Year</div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#f59e0b]">Year 1</span>
              <span className={`text-lg font-semibold ${calculations.y1Total >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                {formatCurrency(calculations.y1Total, true)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#0ea5e9]">Year 2</span>
              <span className={`text-lg font-semibold ${calculations.y2Total >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                {formatCurrency(calculations.y2Total, true)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#10b981]">Year 3</span>
              <span className={`text-lg font-semibold ${calculations.y3Total >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                {formatCurrency(calculations.y3Total, true)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Charts */}
      <div className="flex-1 flex flex-col gap-4 overflow-visible">
        {/* Cash Position Chart - Smaller */}
        <div
          className="rounded-lg p-4 overflow-visible"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)', height: '45%' }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-[#64748b] uppercase tracking-wider">Cash Position</div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#10b981]" />
              <span className="text-xs text-[#94a3b8]">Cash Position</span>
            </div>
          </div>

          <div className="h-[calc(100%-2rem)] flex overflow-visible">
            {/* Y-axis */}
            <div className="w-14 flex flex-col justify-between text-xs text-[#64748b] pb-6 pr-2 text-right">
              <span>{formatCurrency(chartMax, true)}</span>
              <span className={chartMin < 0 ? 'text-[#ef4444]' : ''}>{formatCurrency(chartMin, true)}</span>
            </div>

            {/* Chart */}
            <div className="flex-1 flex flex-col overflow-visible">
              {/* Chart area */}
              <div className="flex-1 relative overflow-visible">
                {/* Year dividers */}
                <div className="absolute top-0 bottom-0 left-[33.33%] border-l border-white/20" />
                <div className="absolute top-0 bottom-0 left-[66.66%] border-l border-white/20" />

                {/* Zero line */}
                {chartMin < 0 && (
                  <div
                    className="absolute left-0 right-0 border-t-2 border-dashed border-[#ef4444]/50 z-10"
                    style={{ bottom: `${(Math.abs(chartMin) / chartRange) * 100}%` }}
                  >
                    <span className="absolute -left-10 -top-2 text-xs text-[#ef4444] font-medium">$0</span>
                  </div>
                )}

                {/* Bars */}
                <div className="absolute inset-0 flex gap-0.5 overflow-visible">
                  {calculations.quarterData.map((q: any, i: number) => {
                    const height = ((q.cashPosition - chartMin) / chartRange) * 100;
                    const isNegative = q.cashPosition < 0;
                    const isCashOut = q.quarter === calculations.cashOutQuarter;

                    return (
                      <div key={q.quarter} className="flex-1 relative group overflow-visible">
                        <div
                          className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[70%] max-w-[30px] rounded-t transition-all ${isCashOut ? 'ring-2 ring-[#ef4444] ring-offset-1 ring-offset-[#0f172a]' : ''}`}
                          style={{
                            height: `${Math.max(height, 2)}%`,
                            background: isNegative
                              ? 'linear-gradient(180deg, #ef4444 0%, #dc2626 100%)'
                              : i < 4
                                ? 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)'
                                : i < 8
                                  ? 'linear-gradient(180deg, #0ea5e9 0%, #0284c7 100%)'
                                  : 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                          }}
                        />

                        {/* Tooltip */}
                        <div
                          className="absolute left-1/2 -translate-x-1/2 hidden group-hover:block z-[100] bg-[#1e293b] border border-white/20 rounded-lg p-2 text-xs whitespace-nowrap shadow-2xl pointer-events-none"
                          style={{ bottom: `${Math.min(height + 10, 85)}%` }}
                        >
                          <div className="font-bold text-white mb-1">{q.quarter}</div>
                          <div className="text-[#94a3b8]">Cash: <span className="text-white font-medium">{formatCurrency(q.cashPosition)}</span></div>
                        </div>

                        {isCashOut && (
                          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-[#ef4444] rounded text-[10px] text-white font-bold shadow-lg z-30">
                            OUT
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* X-axis labels */}
              <div className="h-6 flex-shrink-0 flex">
                {calculations.quarterData.map((_q: any, i: number) => (
                  <div key={i} className="flex-1 text-center text-[10px] text-[#64748b]">
                    Q{(i % 4) + 1}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Stacked Area Chart */}
        <div
          className="rounded-lg p-4 overflow-visible flex-1"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-[#64748b] uppercase tracking-wider">Quarterly Revenue</div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-[#3b82f6]" />
                <span className="text-xs text-[#94a3b8]">B2C</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-[#10b981]" />
                <span className="text-xs text-[#94a3b8]">B2B</span>
              </div>
            </div>
          </div>

          <div className="h-[calc(100%-2rem)] flex overflow-visible">
            {/* Y-axis */}
            <div className="w-14 flex flex-col justify-between text-xs text-[#64748b] pb-6 pr-2 text-right">
              <span>{formatCurrency(revenueChartMax, true)}</span>
              <span>{formatCurrency(revenueChartMax / 2, true)}</span>
              <span>$0</span>
            </div>

            {/* Chart */}
            <div className="flex-1 flex flex-col overflow-visible">
              {/* Year labels */}
              <div className="relative h-4 flex-shrink-0">
                <div className="absolute left-[16.66%] -translate-x-1/2 text-xs text-[#f59e0b] font-semibold">Year 1</div>
                <div className="absolute left-[50%] -translate-x-1/2 text-xs text-[#0ea5e9] font-semibold">Year 2</div>
                <div className="absolute left-[83.33%] -translate-x-1/2 text-xs text-[#10b981] font-semibold">Year 3</div>
              </div>

              {/* Chart area */}
              <div className="flex-1 relative overflow-visible">
                {/* Year dividers */}
                <div className="absolute top-0 bottom-0 left-[33.33%] border-l border-white/20" />
                <div className="absolute top-0 bottom-0 left-[66.66%] border-l border-white/20" />

                {/* Stacked Area SVG */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {(() => {
                    const scaleY = (val: number) => 100 - (val / revenueChartMax) * 100;

                    // B2B area (bottom layer)
                    const b2bPoints = calculations.quarterData.map((q: any, i: number) => {
                      const x = (i / 11) * 100;
                      const y = scaleY(q.b2bRevenue);
                      return `${x},${y}`;
                    });
                    const b2bPath = `M 0,100 L ${b2bPoints.join(' L ')} L 100,100 Z`;

                    // B2C area (stacked on top of B2B)
                    const b2cPoints = calculations.quarterData.map((q: any, i: number) => {
                      const x = (i / 11) * 100;
                      const y = scaleY(q.b2cRevenue + q.b2bRevenue);
                      return `${x},${y}`;
                    });
                    const b2bTopPoints = [...calculations.quarterData].reverse().map((q: any, i: number) => {
                      const x = ((11 - i) / 11) * 100;
                      const y = scaleY(q.b2bRevenue);
                      return `${x},${y}`;
                    });
                    const b2cPath = `M ${b2cPoints.join(' L ')} L ${b2bTopPoints.join(' L ')} Z`;

                    return (
                      <>
                        {/* B2B fill */}
                        <path d={b2bPath} fill="rgba(16, 185, 129, 0.6)" />
                        {/* B2C fill */}
                        <path d={b2cPath} fill="rgba(59, 130, 246, 0.6)" />
                        {/* B2B line */}
                        <path
                          d={`M ${b2bPoints.join(' L ')}`}
                          fill="none"
                          stroke="#10b981"
                          strokeWidth="2"
                          vectorEffect="non-scaling-stroke"
                        />
                        {/* Total line (top of stack) */}
                        <path
                          d={`M ${b2cPoints.join(' L ')}`}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="2"
                          vectorEffect="non-scaling-stroke"
                        />
                      </>
                    );
                  })()}
                </svg>

                {/* Hover areas for tooltips */}
                <div className="absolute inset-0 flex">
                  {calculations.quarterData.map((q: any) => (
                    <div key={q.quarter} className="flex-1 relative group">
                      <div
                        className="absolute left-1/2 -translate-x-1/2 top-2 hidden group-hover:block z-[100] bg-[#1e293b] border border-white/20 rounded-lg p-2 text-xs whitespace-nowrap shadow-2xl pointer-events-none"
                      >
                        <div className="font-bold text-white mb-1">{q.quarter}</div>
                        <div className="text-[#94a3b8]">B2C: <span className="text-[#3b82f6] font-medium">{formatCurrency(q.b2cRevenue)}</span></div>
                        <div className="text-[#94a3b8]">B2B: <span className="text-[#10b981] font-medium">{formatCurrency(q.b2bRevenue)}</span></div>
                        <div className="text-[#94a3b8] border-t border-white/10 pt-1 mt-1">Total: <span className="text-white font-medium">{formatCurrency(q.totalRevenue)}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* X-axis labels */}
              <div className="h-6 flex-shrink-0 flex">
                {calculations.quarterData.map((_q: any, i: number) => (
                  <div key={i} className="flex-1 text-center text-[10px] text-[#64748b]">
                    Q{(i % 4) + 1}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// FTE Circle Component - shows fill level and handles click
const FTECircle: React.FC<{
  value: number;
  maxValue?: number;
  onClick: (e: React.MouseEvent) => void;
  colorClass: string;
}> = ({ value, maxValue = 4, onClick, colorClass }) => {
  // For display, we show up to maxValue circles worth
  const displayValue = Math.min(value, maxValue);
  const fillPercent = (displayValue / maxValue) * 100;

  return (
    <div
      onClick={onClick}
      className="relative w-10 h-10 cursor-pointer select-none group"
      title={`${value} FTE - Click to increase, Ctrl+Click to decrease`}
    >
      {/* Background circle */}
      <div className="absolute inset-0 rounded-full border-2 border-white/20 bg-white/5" />

      {/* Filled segments using conic gradient */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: value > 0
            ? `conic-gradient(${colorClass} ${fillPercent}%, transparent ${fillPercent}%)`
            : 'transparent',
        }}
      />

      {/* Quarter segment lines */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 w-px h-1/2 bg-white/20 origin-bottom" />
        <div className="absolute top-1/2 right-0 w-1/2 h-px bg-white/20 origin-left" />
        <div className="absolute bottom-0 left-1/2 w-px h-1/2 bg-white/20 origin-top" />
        <div className="absolute top-1/2 left-0 w-1/2 h-px bg-white/20 origin-right" />
      </div>

      {/* Center circle with value */}
      <div className="absolute inset-2 rounded-full bg-[#0f172a] flex items-center justify-center">
        <span className="text-xs font-bold text-white">{value}</span>
      </div>

      {/* Hover indicator */}
      <div className="absolute inset-0 rounded-full border-2 border-transparent group-hover:border-white/40 transition-colors" />
    </div>
  );
};

// Staff Tab Component
const StaffTab: React.FC<{
  positions: StaffPosition[];
  onUpdateFTE: (id: string, quarter: number, delta: number, singleCell: boolean) => void;
  onUpdateSalary: (id: string, salary: number) => void;
  onUpdateYearlyIncrease: (id: string, percent: number) => void;
  onUpdateName: (id: string, name: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  formatCurrency: (v: number, short?: boolean) => string;
}> = ({ positions, onUpdateFTE, onUpdateSalary, onUpdateYearlyIncrease, onUpdateName, onAdd, onRemove, formatCurrency }) => {

  const handleCircleClick = (positionId: string, quarterIndex: number, _currentValue: number, e: React.MouseEvent) => {
    e.preventDefault();
    const isDecrease = e.ctrlKey || e.metaKey;
    const singleCell = e.shiftKey;
    const delta = isDecrease ? -0.25 : 0.25;
    // Shift+Click = single cell, Shift+Ctrl/Cmd+Click = single cell decrease
    onUpdateFTE(positionId, quarterIndex, delta, singleCell);
  };

  const getYearColor = (quarterIndex: number) => {
    if (quarterIndex < 4) return '#f59e0b';
    if (quarterIndex < 8) return '#0ea5e9';
    return '#10b981';
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-[#94a3b8]">
          <span className="text-white font-medium">Click</span> to increase, <span className="text-white font-medium">Ctrl+Click</span> to decrease, <span className="text-white font-medium">Shift+Click</span> single cell only
        </div>
        <button
          onClick={onAdd}
          className="px-4 py-2 text-sm bg-[#8b5cf6] hover:bg-[#7c3aed] text-white rounded-lg transition-colors font-medium"
        >
          + Add Position
        </button>
      </div>

      <div
        className="flex-1 rounded-lg overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="overflow-auto h-full p-4">
          {/* Year headers */}
          <div className="flex mb-2 ml-[280px]">
            <div className="flex-1 text-center">
              <span className="text-base font-semibold text-[#f59e0b]">Year 1</span>
            </div>
            <div className="flex-1 text-center border-l border-white/20">
              <span className="text-base font-semibold text-[#0ea5e9]">Year 2</span>
            </div>
            <div className="flex-1 text-center border-l border-white/20">
              <span className="text-base font-semibold text-[#10b981]">Year 3</span>
            </div>
          </div>

          {/* Quarter headers */}
          <div className="flex mb-4 ml-[280px]">
            {QUARTERS.map((q, i) => (
              <div
                key={q}
                className={`flex-1 text-center text-sm text-[#64748b] ${i === 4 || i === 8 ? 'border-l border-white/20' : ''}`}
              >
                Q{(i % 4) + 1}
              </div>
            ))}
          </div>

          {/* Staff rows */}
          <div className="space-y-3">
            {positions.map((pos) => (
              <div key={pos.id} className="flex items-center gap-4 py-2 border-b border-white/5 hover:bg-white/5 rounded-lg px-2">
                {/* Position info */}
                <div className="w-[260px] flex items-center gap-3">
                  <button
                    onClick={() => onRemove(pos.id)}
                    className="text-[#ef4444] hover:text-[#dc2626] transition-colors text-lg font-bold w-6"
                  >
                    ×
                  </button>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={pos.name}
                      onChange={(e) => onUpdateName(pos.id, e.target.value)}
                      className="w-full bg-transparent text-white text-base font-medium border-none focus:outline-none focus:ring-1 focus:ring-[#8b5cf6] rounded px-1"
                    />
                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-[#64748b]">$</span>
                        <input
                          type="number"
                          value={pos.baseSalary}
                          onChange={(e) => onUpdateSalary(pos.id, parseInt(e.target.value) || 0)}
                          className="w-20 bg-transparent text-sm text-[#10b981] border-none focus:outline-none focus:ring-1 focus:ring-[#8b5cf6] rounded px-1"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-[#64748b]">+</span>
                        <input
                          type="number"
                          step={0.5}
                          value={pos.yearlyIncreasePercent}
                          onChange={(e) => onUpdateYearlyIncrease(pos.id, parseFloat(e.target.value) || 0)}
                          className="w-10 bg-transparent text-sm text-[#a78bfa] border-none focus:outline-none focus:ring-1 focus:ring-[#8b5cf6] rounded px-1"
                        />
                        <span className="text-xs text-[#64748b]">%/yr</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FTE circles */}
                <div className="flex-1 flex">
                  {pos.fteByQuarter.map((fte, i) => (
                    <div
                      key={i}
                      className={`flex-1 flex justify-center ${i === 4 || i === 8 ? 'border-l border-white/20' : ''}`}
                    >
                      <FTECircle
                        value={fte}
                        onClick={(e) => handleCircleClick(pos.id, i, fte, e)}
                        colorClass={getYearColor(i)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Totals row */}
          <div className="flex items-center gap-4 mt-6 pt-4 border-t border-white/20">
            <div className="w-[260px] text-white font-semibold text-base">
              Total Quarterly Cost
            </div>
            <div className="flex-1 flex">
              {QUARTERS.map((_, i) => {
                const year = Math.floor(i / 4);
                const total = positions.reduce((sum, pos) => {
                  const adjustedSalary = pos.baseSalary * Math.pow(1 + pos.yearlyIncreasePercent / 100, year);
                  return sum + (adjustedSalary / 4 * pos.fteByQuarter[i]);
                }, 0);
                return (
                  <div
                    key={i}
                    className={`flex-1 text-center text-base font-semibold ${i === 4 || i === 8 ? 'border-l border-white/20' : ''}`}
                    style={{ color: getYearColor(i) }}
                  >
                    {formatCurrency(total, true)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Revenue Bar Component - visual bar showing value with click to adjust
const RevenueBar: React.FC<{
  value: number;
  maxValue: number;
  onClick: (e: React.MouseEvent) => void;
  color: string;
  label?: string;
  increment: number;
}> = ({ value, maxValue, onClick, color, increment }) => {
  const fillPercent = Math.min((value / maxValue) * 100, 100);
  const displayValue = value >= 1000 ? `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}K` : value;

  return (
    <div
      onClick={onClick}
      className="relative h-10 cursor-pointer select-none group"
      title={`Click +${increment}, Ctrl+Click -${increment}`}
    >
      {/* Bar background */}
      <div className="h-full rounded bg-white/10 relative overflow-hidden flex items-center justify-center">
        {/* Filled portion */}
        <div
          className="absolute inset-y-0 left-0 rounded transition-all"
          style={{
            width: `${fillPercent}%`,
            background: `linear-gradient(90deg, ${color} 0%, ${color}88 100%)`,
          }}
        />
        {/* Value centered on bar */}
        <span className="relative z-10 text-sm font-bold text-white drop-shadow-md">
          {displayValue}
        </span>
        {/* Hover effect */}
        <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors" />
      </div>
    </div>
  );
};

// Revenue Tab Component
const RevenueTab: React.FC<{
  settings: RevenueSettings;
  onUpdate: (settings: RevenueSettings) => void;
  formatCurrency: (v: number, short?: boolean) => string;
}> = ({ settings, onUpdate, formatCurrency }) => {
  const handleCascadingUpdate = (key: keyof RevenueSettings, index: number, increment: number, e: React.MouseEvent) => {
    e.preventDefault();
    const isDecrease = e.ctrlKey || e.metaKey;
    const singleCell = e.shiftKey;
    const delta = isDecrease ? -increment : increment;

    // Update quarters based on single cell or cascading mode
    const newArray = settings[key].map((v, i) => {
      if (singleCell) {
        return i === index ? Math.max(0, v + delta) : v;
      }
      return i >= index ? Math.max(0, v + delta) : v;
    });
    onUpdate({ ...settings, [key]: newArray });
  };

  // Calculate derived values
  const quarterlyRevenue = QUARTERS.map((_, i) => {
    const b2c = settings.b2cSubsByQuarter[i] * settings.b2cMonthlyFee[i] * 3;
    const b2b = settings.b2bSchoolsByQuarter[i] * settings.avgStudentsPerSchool[i] * settings.b2bFeePerStudent[i] * 3;
    return { b2c, b2b, total: b2c + b2b };
  });

  const getYearColor = (quarterIndex: number) => {
    if (quarterIndex < 4) return '#f59e0b';
    if (quarterIndex < 8) return '#0ea5e9';
    return '#10b981';
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-[#94a3b8]">
          <span className="text-white font-medium">Click</span> to increase, <span className="text-white font-medium">Ctrl+Click</span> to decrease, <span className="text-white font-medium">Shift+Click</span> single cell only
        </div>
      </div>

      <div
        className="flex-1 rounded-lg overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="overflow-auto h-full p-4">
          {/* Year headers */}
          <div className="flex mb-2 ml-[200px]">
            <div className="flex-1 text-center">
              <span className="text-base font-semibold text-[#f59e0b]">Year 1</span>
            </div>
            <div className="flex-1 text-center border-l border-white/20">
              <span className="text-base font-semibold text-[#0ea5e9]">Year 2</span>
            </div>
            <div className="flex-1 text-center border-l border-white/20">
              <span className="text-base font-semibold text-[#10b981]">Year 3</span>
            </div>
          </div>

          {/* Quarter headers */}
          <div className="flex mb-4 ml-[200px]">
            {QUARTERS.map((q, i) => (
              <div
                key={q}
                className={`flex-1 text-center text-sm text-[#64748b] ${i === 4 || i === 8 ? 'border-l border-white/20' : ''}`}
              >
                Q{(i % 4) + 1}
              </div>
            ))}
          </div>

          {/* B2C Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-[#0ea5e9]" />
              <span className="text-base font-semibold text-[#0ea5e9]">B2C Consumer</span>
            </div>

            {/* B2C Subscribers */}
            <div className="flex items-center gap-4 py-2 border-b border-white/5">
              <div className="w-[180px]">
                <div className="text-white font-medium">Subscribers</div>
                <div className="text-xs text-[#64748b]">Click ±500</div>
              </div>
              <div className="flex-1 flex gap-1">
                {settings.b2cSubsByQuarter.map((val, i) => (
                  <div key={i} className={`flex-1 ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    <RevenueBar
                      value={val}
                      maxValue={40000}
                      onClick={(e) => handleCascadingUpdate('b2cSubsByQuarter', i, 500, e)}
                      color={getYearColor(i)}
                      label="Subscribers"
                      increment={500}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* B2C Monthly Fee */}
            <div className="flex items-center gap-4 py-2 border-b border-white/5">
              <div className="w-[180px]">
                <div className="text-white font-medium">Monthly Fee</div>
                <div className="text-xs text-[#64748b]">Click ±$5</div>
              </div>
              <div className="flex-1 flex gap-1">
                {settings.b2cMonthlyFee.map((val, i) => (
                  <div key={i} className={`flex-1 ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    <div
                      onClick={(e) => handleCascadingUpdate('b2cMonthlyFee', i, 5, e)}
                      className="h-10 flex items-center justify-center cursor-pointer group"
                    >
                      <div
                        className="px-3 py-1 rounded-full font-bold text-lg group-hover:scale-110 transition-transform"
                        style={{ background: `${getYearColor(i)}33`, color: getYearColor(i) }}
                      >
                        ${val}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* B2C Revenue */}
            <div className="flex items-center gap-4 py-2 bg-white/5 rounded">
              <div className="w-[180px]">
                <div className="text-[#0ea5e9] font-semibold">B2C Revenue</div>
              </div>
              <div className="flex-1 flex gap-1">
                {quarterlyRevenue.map((q, i) => (
                  <div key={i} className={`flex-1 text-center ${i === 4 || i === 8 ? 'border-l border-white/20' : ''}`}>
                    <span className="text-base font-bold text-[#0ea5e9]">{formatCurrency(q.b2c, true)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* B2B Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-[#10b981]" />
              <span className="text-base font-semibold text-[#10b981]">B2B Enterprise</span>
            </div>

            {/* B2B Schools */}
            <div className="flex items-center gap-4 py-2 border-b border-white/5">
              <div className="w-[180px]">
                <div className="text-white font-medium">Schools</div>
                <div className="text-xs text-[#64748b]">Click ±10</div>
              </div>
              <div className="flex-1 flex gap-1">
                {settings.b2bSchoolsByQuarter.map((val, i) => (
                  <div key={i} className={`flex-1 ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    <RevenueBar
                      value={val}
                      maxValue={400}
                      onClick={(e) => handleCascadingUpdate('b2bSchoolsByQuarter', i, 10, e)}
                      color={getYearColor(i)}
                      label="Schools"
                      increment={10}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Avg Students per School */}
            <div className="flex items-center gap-4 py-2 border-b border-white/5">
              <div className="w-[180px]">
                <div className="text-white font-medium">Avg Students/School</div>
                <div className="text-xs text-[#64748b]">Click ±50</div>
              </div>
              <div className="flex-1 flex gap-1">
                {settings.avgStudentsPerSchool.map((val, i) => (
                  <div key={i} className={`flex-1 ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    <div
                      onClick={(e) => handleCascadingUpdate('avgStudentsPerSchool', i, 50, e)}
                      className="h-10 flex items-center justify-center cursor-pointer group"
                    >
                      <div
                        className="px-2 py-1 rounded font-bold text-sm group-hover:scale-110 transition-transform"
                        style={{ background: `${getYearColor(i)}33`, color: getYearColor(i) }}
                      >
                        {val}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* B2B Fee per Student */}
            <div className="flex items-center gap-4 py-2 border-b border-white/5">
              <div className="w-[180px]">
                <div className="text-white font-medium">Fee/Student/Month</div>
                <div className="text-xs text-[#64748b]">Click ±$1</div>
              </div>
              <div className="flex-1 flex gap-1">
                {settings.b2bFeePerStudent.map((val, i) => (
                  <div key={i} className={`flex-1 ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    <div
                      onClick={(e) => handleCascadingUpdate('b2bFeePerStudent', i, 1, e)}
                      className="h-10 flex items-center justify-center cursor-pointer group"
                    >
                      <div
                        className="px-3 py-1 rounded-full font-bold text-lg group-hover:scale-110 transition-transform"
                        style={{ background: `${getYearColor(i)}33`, color: getYearColor(i) }}
                      >
                        ${val}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* B2B Revenue */}
            <div className="flex items-center gap-4 py-2 bg-white/5 rounded">
              <div className="w-[180px]">
                <div className="text-[#10b981] font-semibold">B2B Revenue</div>
              </div>
              <div className="flex-1 flex gap-1">
                {quarterlyRevenue.map((q, i) => (
                  <div key={i} className={`flex-1 text-center ${i === 4 || i === 8 ? 'border-l border-white/20' : ''}`}>
                    <span className="text-base font-bold text-[#10b981]">{formatCurrency(q.b2b, true)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="flex items-center gap-4 py-3 mt-4 border-t border-white/20">
            <div className="w-[180px]">
              <div className="text-white font-bold text-lg">Total Revenue</div>
            </div>
            <div className="flex-1 flex gap-1">
              {quarterlyRevenue.map((q, i) => (
                <div key={i} className={`flex-1 text-center ${i === 4 || i === 8 ? 'border-l border-white/20' : ''}`}>
                  <span className="text-lg font-bold text-white">{formatCurrency(q.total, true)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Clickable Cost Cell Component
const CostCell: React.FC<{
  value: number;
  onChange: (delta: number, singleCell: boolean) => void;
  increment: number;
  color: string;
  formatValue: (v: number) => string;
}> = ({ value, onChange, increment, color, formatValue }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const isDecrease = e.ctrlKey || e.metaKey;
    const singleCell = e.shiftKey;
    const delta = isDecrease ? -increment : increment;
    onChange(delta, singleCell);
  };

  return (
    <div
      onClick={handleClick}
      className="h-8 flex items-center justify-center cursor-pointer select-none group rounded"
      style={{ background: 'rgba(255,255,255,0.03)' }}
      title={`Click: +${increment} (cascade), Ctrl: -${increment} (cascade), Shift: single cell, Shift+Ctrl: single cell decrease`}
    >
      <span
        className="text-xs font-medium group-hover:scale-110 transition-transform"
        style={{ color }}
      >
        {formatValue(value)}
      </span>
    </div>
  );
};

// Costs Tab Component
const CostsTab: React.FC<{
  settings: CostSettings;
  onUpdate: (settings: CostSettings) => void;
  formatCurrency: (v: number, short?: boolean) => string;
  quarterlyData: { newSubs: number; digitalAdvertising: number }[];
}> = ({ settings, onUpdate, formatCurrency, quarterlyData }) => {
  const handleCostChange = (key: keyof CostSettings, index: number, delta: number, singleCell: boolean) => {
    if (Array.isArray(settings[key])) {
      const newArray = (settings[key] as number[]).map((v, i) => {
        if (singleCell) {
          return i === index ? Math.max(0, v + delta) : v;
        }
        return i >= index ? Math.max(0, v + delta) : v;
      });
      onUpdate({ ...settings, [key]: newArray });
    }
  };

  const getYearColor = (quarterIndex: number) => {
    if (quarterIndex < 4) return '#f59e0b';
    if (quarterIndex < 8) return '#0ea5e9';
    return '#10b981';
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-[#94a3b8]">
          <span className="text-white font-medium">Click</span> to increase, <span className="text-white font-medium">Ctrl+Click</span> to decrease, <span className="text-white font-medium">Shift+Click</span> single cell only
        </div>
      </div>

      <div
        className="flex-1 rounded-lg overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="overflow-auto h-full p-4">
          {/* Quarter Headers */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-[200px]" />
            <div className="flex-1 flex gap-1">
              {QUARTERS.map((q, i) => (
                <div
                  key={q}
                  className={`flex-1 text-center text-xs font-medium ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}
                  style={{ color: getYearColor(i) }}
                >
                  Q{(i % 4) + 1}
                </div>
              ))}
            </div>
          </div>

          {/* Sales & Marketing Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-[#f59e0b]" />
              <span className="text-base font-semibold text-[#f59e0b]">Sales & Marketing</span>
            </div>

            {/* B2C CPA */}
            <div className="flex items-center gap-4 py-2 border-b border-white/5">
              <div className="w-[200px]">
                <div className="text-white font-medium">B2C Cost Per Acquisition</div>
                <div className="text-xs text-[#64748b]">Click ±$5</div>
              </div>
              <div className="flex-1 flex gap-1">
                {settings.b2cCostPerAcquisition.map((val, i) => (
                  <div key={i} className={`flex-1 ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    <CostCell
                      value={val}
                      onChange={(delta, single) => handleCostChange('b2cCostPerAcquisition', i, delta, single)}
                      increment={5}
                      color={getYearColor(i)}
                      formatValue={(v) => `$${v}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* New Subscribers (read-only) */}
            <div className="flex items-center gap-4 py-1 border-b border-white/5">
              <div className="w-[200px]">
                <div className="text-[#64748b] text-xs">↳ New Subscribers</div>
              </div>
              <div className="flex-1 flex gap-1">
                {quarterlyData.map((q, i) => (
                  <div key={i} className={`flex-1 text-center text-xs text-[#64748b] ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    {q.newSubs.toLocaleString()}
                  </div>
                ))}
              </div>
            </div>

            {/* Digital Advertising (calculated) */}
            <div className="flex items-center gap-4 py-2 bg-white/5 rounded mb-3">
              <div className="w-[200px]">
                <div className="text-[#f59e0b] font-semibold">= Digital Advertising</div>
              </div>
              <div className="flex-1 flex gap-1">
                {quarterlyData.map((q, i) => (
                  <div key={i} className={`flex-1 text-center ${i === 4 || i === 8 ? 'border-l border-white/20' : ''}`}>
                    <span className="text-sm font-bold text-[#f59e0b]">{formatCurrency(q.digitalAdvertising, true)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* B2B Outreach */}
            <div className="flex items-center gap-4 py-2 border-b border-white/5">
              <div className="w-[200px]">
                <div className="text-white font-medium">B2B Outreach & Conferences</div>
                <div className="text-xs text-[#64748b]">Click ±$500</div>
              </div>
              <div className="flex-1 flex gap-1">
                {settings.b2bOutreach.map((val, i) => (
                  <div key={i} className={`flex-1 ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    <CostCell
                      value={val}
                      onChange={(delta, single) => handleCostChange('b2bOutreach', i, delta, single)}
                      increment={500}
                      color={getYearColor(i)}
                      formatValue={(v) => formatCurrency(v, true)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* General & Administrative Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-[#64748b]" />
              <span className="text-base font-semibold text-[#94a3b8]">General & Administrative</span>
            </div>

            {/* Office & General */}
            <div className="flex items-center gap-4 py-2 border-b border-white/5">
              <div className="w-[200px]">
                <div className="text-white font-medium">Office & General</div>
                <div className="text-xs text-[#64748b]">Click ±$1K</div>
              </div>
              <div className="flex-1 flex gap-1">
                {settings.officeExpenses.map((val, i) => (
                  <div key={i} className={`flex-1 ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    <CostCell
                      value={val}
                      onChange={(delta, single) => handleCostChange('officeExpenses', i, delta, single)}
                      increment={1000}
                      color={getYearColor(i)}
                      formatValue={(v) => formatCurrency(v, true)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Software & IT */}
            <div className="flex items-center gap-4 py-2 border-b border-white/5">
              <div className="w-[200px]">
                <div className="text-white font-medium">Software & IT</div>
                <div className="text-xs text-[#64748b]">Click ±$500</div>
              </div>
              <div className="flex-1 flex gap-1">
                {settings.softwareSubscriptions.map((val, i) => (
                  <div key={i} className={`flex-1 ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    <CostCell
                      value={val}
                      onChange={(delta, single) => handleCostChange('softwareSubscriptions', i, delta, single)}
                      increment={500}
                      color={getYearColor(i)}
                      formatValue={(v) => formatCurrency(v, true)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Legal & Accounting */}
            <div className="flex items-center gap-4 py-2 border-b border-white/5">
              <div className="w-[200px]">
                <div className="text-white font-medium">Legal & Accounting</div>
                <div className="text-xs text-[#64748b]">Click ±$2.5K</div>
              </div>
              <div className="flex-1 flex gap-1">
                {settings.legalAccounting.map((val, i) => (
                  <div key={i} className={`flex-1 ${i === 4 || i === 8 ? 'border-l border-white/20 pl-1' : ''}`}>
                    <CostCell
                      value={val}
                      onChange={(delta, single) => handleCostChange('legalAccounting', i, delta, single)}
                      increment={2500}
                      color={getYearColor(i)}
                      formatValue={(v) => formatCurrency(v, true)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Total Row */}
          <div className="flex items-center gap-4 py-3 border-t border-white/20">
            <div className="w-[200px]">
              <div className="text-white font-bold text-lg">Total (excl. Staff)</div>
            </div>
            <div className="flex-1 flex gap-1">
              {QUARTERS.map((_, i) => {
                const total =
                  quarterlyData[i].digitalAdvertising +
                  settings.b2bOutreach[i] +
                  settings.officeExpenses[i] +
                  settings.softwareSubscriptions[i] +
                  settings.legalAccounting[i];
                return (
                  <div key={i} className={`flex-1 text-center ${i === 4 || i === 8 ? 'border-l border-white/20' : ''}`}>
                    <span className="text-lg font-bold text-white">{formatCurrency(total, true)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Settings Tab Component
const SettingsTab: React.FC<{
  costSettings: CostSettings;
  onUpdateCosts: (settings: CostSettings) => void;
}> = ({ costSettings, onUpdateCosts }) => {
  return (
    <div className="h-full flex flex-col gap-4">
      <div className="text-xs text-[#64748b]">Global settings and unit economics</div>

      <div className="grid grid-cols-3 gap-4">
        {/* COGS Settings */}
        <div
          className="p-4 rounded"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="text-sm text-white font-medium mb-3">Cost of Goods Sold</div>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#64748b] block mb-1">Cloud Hosting ($/user/qtr)</label>
              <input
                type="number"
                step="0.01"
                value={costSettings.cloudHostingPerUser}
                onChange={(e) => onUpdateCosts({ ...costSettings, cloudHostingPerUser: parseFloat(e.target.value) || 0 })}
                className="w-full bg-[#1e293b] text-white border border-white/10 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#8b5cf6]"
              />
            </div>
            <div>
              <label className="text-xs text-[#64748b] block mb-1">AI Tokens ($/user/qtr)</label>
              <input
                type="number"
                step="0.01"
                value={costSettings.aiTokenPerUser}
                onChange={(e) => onUpdateCosts({ ...costSettings, aiTokenPerUser: parseFloat(e.target.value) || 0 })}
                className="w-full bg-[#1e293b] text-white border border-white/10 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#8b5cf6]"
              />
            </div>
            <div>
              <label className="text-xs text-[#64748b] block mb-1">Payment Processing (%)</label>
              <input
                type="number"
                step="0.1"
                value={costSettings.paymentProcessingRate}
                onChange={(e) => onUpdateCosts({ ...costSettings, paymentProcessingRate: parseFloat(e.target.value) || 0 })}
                className="w-full bg-[#1e293b] text-white border border-white/10 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#8b5cf6]"
              />
            </div>
          </div>
        </div>

        {/* Info Panel */}
        <div
          className="p-4 rounded col-span-2"
          style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)' }}
        >
          <div className="text-sm text-[#a78bfa] font-medium mb-3">How This Model Works</div>
          <ul className="text-xs text-[#94a3b8] space-y-2">
            <li><span className="text-white font-medium">Staff Tab:</span> Add/remove positions, set annual salaries, and control FTE count per quarter</li>
            <li><span className="text-white font-medium">Revenue Tab:</span> Configure B2C subscribers and fees, B2B schools and per-student pricing</li>
            <li><span className="text-white font-medium">Costs Tab:</span> Set marketing spend, office costs, and professional fees per quarter</li>
            <li><span className="text-white font-medium">Summary Tab:</span> See the aggregated cash flow, burn rate, and runway based on all inputs</li>
          </ul>
          <div className="mt-4 pt-4 border-t border-[#8b5cf6]/30">
            <div className="text-xs text-[#a78bfa]">Data initialized from Lumen8 3Yr Forecast spreadsheet</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GTMv2Slide05FinancialCalculator;
